import React, { useCallback, useEffect, useState } from 'react';

import ModalDocumentManager from './ModalDocumentManager';
import { EmployeeVoucher, Contract } from '../../../models/employee.model';
import { EmployeeDA } from '../../../services/admin/employee.service';
import { ContractDA } from '../../../services/admin/contract.service';
import { MediaDA } from '../../../services/admin/media.service';
import { usePdfViewer } from '../../../contexts/PdfViewerContext';
import { attachmentPdfFilename } from '../../../scripts/document.filename.script';
import { datetimeFormat12h } from '../../../scripts/date.time.format.script';
import { GlobalSwal } from '../../../styles/swal.style';
import { getError } from '../../../languages/translations/response';

const DOCUMENT_ROLES = [
  'employment_contract',
  'amendment',
  'identity',
  'residence_permit',
  'certificate',
  'other',
] as const;

const DOC_MODAL_ID = 'modal-employee-documents';

interface EmployeeDocumentsProps {
  companyId: string;
  employeeId: string;
  vouchers: EmployeeVoucher[];
  lang: any;
  onChanged: () => void;
}

function roleLabel(lang: any, role?: string | null): string {
  const map: Record<string, string> = {
    employment_contract: lang.labels.roleEmploymentContract || 'Employment contract',
    amendment: lang.labels.roleAmendment || 'Amendment',
    identity: lang.labels.roleIdentity || 'Identity',
    residence_permit: lang.labels.roleResidencePermit || 'Residence permit',
    certificate: lang.labels.roleCertificate || 'Certificate',
    other: lang.labels.roleOther || 'Other',
  };
  return map[role ?? ''] ?? role ?? '-';
}

function roleBadgeClass(role?: string | null): string {
  switch (role) {
    case 'employment_contract':
    case 'amendment':
      return 'badge-light-primary';
    case 'identity':
    case 'residence_permit':
      return 'badge-light-info';
    case 'certificate':
      return 'badge-light-success';
    default:
      return 'badge-light-secondary';
  }
}

function formatSize(bytes?: number | null): string {
  if (!bytes) return '-';
  const k = 1024;
  const sizes = ['B', 'Ko', 'Mo'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

const EmployeeDocuments: React.FC<EmployeeDocumentsProps> = ({
  companyId,
  employeeId,
  vouchers,
  lang,
  onChanged,
}) => {
  const { openUrl } = usePdfViewer();

  const [loadingDoc, setLoadingDoc] = useState<string | null>(null);
  const [contractMap, setContractMap] = useState<Map<string, number>>(new Map());
  const [docModalKey, setDocModalKey] = useState(0);

  // Load contracts to identify which vouchers come from contracts
  const buildContractMap = useCallback(async () => {
    const res = await ContractDA.getContracts(companyId, employeeId);
    if (res.status === 200) {
      const contracts: Contract[] = Array.isArray(res.data) ? res.data : res.data?.items ?? [];
      const map = new Map<string, number>();
      for (const c of contracts) {
        for (const v of c.vouchers ?? []) {
          if (v.id_document_inbox) {
            map.set(v.id_document_inbox, c.version ?? 0);
          }
        }
      }
      setContractMap(map);
    }
  }, [companyId, employeeId]);

  useEffect(() => {
    buildContractMap();
  }, [buildContractMap, vouchers]);

  const handleOpenDocument = async (v: EmployeeVoucher) => {
    if (!v.id_document_inbox) return;
    setLoadingDoc(v.id_document_inbox);
    try {
      const signedUrl = await MediaDA.getDocumentSignedUrl(companyId, v.id_document_inbox);
      if (signedUrl) {
        openUrl(signedUrl, attachmentPdfFilename(v.original_name, 'document'));
      }
    } catch (err) {
      console.error('Failed to open document', err);
    } finally {
      setLoadingDoc(null);
    }
  };

  const handleDelete = async (v: EmployeeVoucher) => {
    if (!v.id_document_inbox) return;

    const isContractDoc = contractMap.has(v.id_document_inbox);
    if (isContractDoc) {
      GlobalSwal.fire({
        text: lang.labels.documentLinkedToContract || 'This document is linked to a contract. Remove it from the contract instead.',
        icon: 'info',
        confirmButtonText: lang.labels.okGotIt,
      });
      return;
    }

    const result = await GlobalSwal.fire({
      text: lang.labels.confirmRemoveDocument || 'Remove this document?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: lang.labels.yesConfirm || 'Yes',
      cancelButtonText: lang.labels.noCancel || 'No',
    });
    if (!result.isConfirmed) return;

    try {
      const res = await EmployeeDA.deleteVoucher(companyId, employeeId, v.id_document_inbox);
      if (res.status === 200) {
        onChanged();
      } else {
        GlobalSwal.fire({
          title: getError(res.data?.code, lang.code, res.data?.message),
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt,
        });
      }
    } catch (err) {
      console.error('Delete failed', err);
    }
  };

  const openDocModal = () => {
    setDocModalKey(k => k + 1);
  };

  return (
    <div className="card mb-5">
      <div className="card-header">
        <div className="card-title">
          <h4 className="fw-bolder">{lang.labels.documents || 'Documents'}</h4>
        </div>
        <div className="card-toolbar">
          <button
            className="btn btn-primary"
            type="button"
            onClick={openDocModal}
          >
            <i className="bi bi-plus-lg me-1"></i>
            {lang.labels.addDocument || 'Add document'}
          </button>
        </div>
      </div>
      <div className="card-body pt-0">
        {(vouchers ?? []).length === 0 ? (
          <p className="text-muted fw-bold text-center py-5">
            {lang.labels.noDocumentsYet || 'No documents yet'}
          </p>
        ) : (
          <div className="table-responsive">
            <table className="table align-middle table-row-dashed mb-2">
              <thead className="border-bottom">
                <tr className="text-start text-muted text-uppercase fw-bolder fs-7">
                  <th className="min-w-200px">{lang.labels.name}</th>
                  <th className="min-w-100px">{lang.labels.role || 'Type'}</th>
                  <th className="min-w-100px">{lang.labels.source || 'Source'}</th>
                  <th className="min-w-80px">{lang.labels.size}</th>
                  <th className="min-w-100px">{lang.labels.date}</th>
                  <th className="w-50px text-end"></th>
                </tr>
              </thead>
              <tbody className="text-secondary fw-bold">
                {(vouchers ?? []).map((v, idx) => {
                  const docId = v.id_document_inbox ?? '';
                  const contractVersion = contractMap.get(docId);
                  const isContractDoc = contractVersion != null;

                  return (
                    <tr key={`${docId}-${idx}`}>
                      <td>
                        <div
                          className="d-flex align-items-center cursor-pointer"
                          onClick={() => handleOpenDocument(v)}
                        >
                          <i className="bi bi-file-earmark-pdf text-danger me-2 fs-4"></i>
                          <span className="fw-bolder text-hover-primary">
                            {loadingDoc === docId
                              ? <span className="spinner-border spinner-border-sm"></span>
                              : v.original_name ?? 'Document'
                            }
                          </span>
                        </div>
                      </td>
                      <td>
                        <span className={`badge ${roleBadgeClass(v.role)} fs-8`}>
                          {roleLabel(lang, v.role)}
                        </span>
                      </td>
                      <td>
                        {isContractDoc ? (
                          <span className="text-primary fw-bold fs-8">
                            <i className="bi bi-file-text me-1"></i>
                            {`${lang.labels.contract || 'Contract'} v${contractVersion}`}
                          </span>
                        ) : (
                          <span className="text-muted fs-8">
                            {lang.labels.personal || 'Personal'}
                          </span>
                        )}
                      </td>
                      <td className="text-muted">{formatSize(v.size)}</td>
                      <td className="text-muted">
                        {v.attached_at ? datetimeFormat12h(lang.code, v.attached_at) : '-'}
                      </td>
                      <td className="text-end">
                        {isContractDoc ? (
                          <i
                            className="bi bi-lock text-muted"
                            title={lang.labels.documentLinkedToContract || 'Linked to contract'}
                          ></i>
                        ) : (
                          <button
                            className="btn btn-sm btn-icon btn-light-danger"
                            title={lang.labels.removeDocument || 'Remove'}
                            onClick={() => handleDelete(v)}
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <ModalDocumentManager
        key={docModalKey}
        modalId={DOC_MODAL_ID}
        title={lang.labels.addDocument || 'Add document'}
        companyId={companyId}
        entityType="employee"
        entityId={employeeId}
        vouchers={vouchers}
        roles={[...DOCUMENT_ROLES]}
        lang={lang}
        onChanged={onChanged}
        showList={false}
        autoOpen={docModalKey > 0}
      />
    </div>
  );
};

export default EmployeeDocuments;
